import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

const TYPES = [["novel", "Book (novel, non-fiction)"], ["comic", "Comic book"], ["manga", "Manga"], ["webtoon", "Webtoon (vertical scroll)"], ["poetry", "Poetry collection"]];
const EMPTY = { format: "novel", title: "", subtitle: "", author: "", publisher: "", series_id: "", book_number: "", genre: "", language: "English", description: "" };

export function BookFormDialog({ open, onOpenChange, book, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [series, setSeries] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/series").then((r) => setSeries(r.data)).catch(() => {});
    setF(book ? { ...EMPTY, ...book, series_id: book.series_id || "", book_number: book.book_number ?? "" } : EMPTY);
  }, [open, book]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setBusy(true);
    const body = { title: f.title, subtitle: f.subtitle, author: f.author, publisher: f.publisher, genre: f.genre, language: f.language, description: f.description,
      ...(book ? {} : { format: f.format }), series_id: f.series_id || null, book_number: f.book_number === "" ? null : Number(f.book_number), preferences: book?.preferences || {} };
    try {
      const { data } = book ? await api.put(`/books/${book.id}`, body) : await api.post("/books", body);
      toast.success(book ? "Book details saved" : "Book created");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label><Input value={f[k] ?? ""} onChange={set(k)} data-testid={`book-form-${k}`} {...props} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#131B2A] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="book-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{book ? "Edit book details" : "New book"}</DialogTitle></DialogHeader>
        {!book && (
          <div className="flex flex-wrap gap-2" data-testid="book-form-type">
            {TYPES.map(([k, l]) => <button key={k} type="button" onClick={() => setF({ ...f, format: k })} className={`text-xs px-3 py-1.5 rounded-full border ${f.format === k ? "border-amber-400 text-amber-200 bg-amber-500/10" : "border-slate-700 text-slate-400"}`} data-testid={`book-type-${k}`}>{l}</button>)}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          {field("title", "Title *")}
          {field("subtitle", "Subtitle")}
          {field("author", "Author")}
          {field("publisher", "Publisher / imprint")}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Series</Label>
            <select value={f.series_id} onChange={set("series_id")} className="w-full h-9 rounded-md bg-transparent border border-input px-3 text-sm" data-testid="book-form-series">
              <option value="" className="bg-slate-900">— Standalone —</option>
              {series.map((s) => <option key={s.id} value={s.id} className="bg-slate-900">{s.name}</option>)}
            </select>
          </div>
          {field("book_number", "Book number in series", { type: "number", step: "0.5" })}
          {field("genre", "Genre")}
          {field("language", "Language")}
          <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs text-slate-400">Description / notes</Label><Textarea value={f.description} onChange={set("description")} data-testid="book-form-description" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-500" data-testid="book-form-save">{book ? "Save" : "Create book"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
